import React, { useState, useEffect } from 'react';
import { gql, useQuery, useMutation, useLazyQuery } from '@apollo/client';

import { TwoFactorLogin } from './components/TwoFactorLogin';
import { UserMessages } from './userMessages';
import { useParams } from 'react-router-dom';

// Cookie key for persisting the token
type StoredToken = { token: string; issued: number };
const COOKIE_NAME = 'AuthorizationMailSystem';
const MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12 hours

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (!match) return undefined;
  try {
    const stored: StoredToken = JSON.parse(decodeURIComponent(match[2]));
    if (Date.now() - stored.issued > MAX_AGE_MS) {
      document.cookie = `${name}=;path=/;SameSite=Strict;Secure;max-age=0`;
      return undefined;
    }
    return stored.token;
  } catch {
    document.cookie = `${name}=;path=/;SameSite=Strict;Secure;max-age=0`;
    return undefined;
  }
}

function setCookie(name: string, value: string) {
  const stored: StoredToken = { token: value, issued: Date.now() };
  const secureFlag = window.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie =
    `${name}=${encodeURIComponent(JSON.stringify(stored))};path=/;SameSite=Strict${secureFlag}`;
}

// --- New: fetch token metadata (messageId + user name) ---
const GET_TOKEN_DATA = gql`
  query GetTokenData($token: String!) {
    getUserTagsByToken(token: $token) {
      name
      tags
      rules
      addresses {
        name
        email
      }
    }
  }
`; // corresponds to getTokenData in schema.ts :contentReference[oaicite:0]{index=0}

const GET_USER_MESSAGE = gql`
  query GetUserMessage($id: ID!, $markRead: Boolean!, $token: String!) {
    getUserMessageByToken(id: $id, markRead: $markRead, token: $token) {
    recipients
    messages {
      id
      senderString      
      recipientString
      subject
      body
      createdAt    
      attachments {
        filename
        mimeType
        data
      }
    }
    }
  }
`;

// Existing list & mutation operations
const LIST_INBOX_BY_TOKEN = gql`
  query ListInboxByToken(
    $token: String!
    $search: String
    $limit: Int
    $offset: Int
    $filter: String
  ) {
    listUserInboxMessagesByToken(
      token: $token
      search: $search
      limit: $limit
      offset: $offset
      filter: $filter
    ) {
      messages {
        id
        senderId
        senderString
        recipientId
        recipientString
        subject
        isRead
        isPinned
        isDeleted
        createdAt  
        tags
       
      }
      pagination {
        limit
        offset
      }
      totalCount
    }
  }
`;

// Dedicated search query for searchUserInboxMessages function
const SEARCH_USER_INBOX_MESSAGES_BY_TOKEN = gql`
  query SearchUserInboxMessagesByToken(
    $token: String!
    $search: String!
    $limit: Int
    $offset: Int
    $filter: String
  ) {
    searchUserInboxMessagesByToken(
      token: $token
      search: $search
      limit: $limit
      offset: $offset
      filter: $filter
    ) {
      messages {
        id
        senderId
        senderString
        recipientId
        recipientString
        subject
        isRead
        isPinned
        isDeleted
        createdAt  
        tags
      }
      pagination {
        limit
        offset
      }
      totalCount
      searchTerm
    }
  }
`;

const LIST_SENT_BY_TOKEN = gql`
  query ListSentMessagesByToken(
    $token: String!
    $search: String
    $limit: Int
    $offset: Int
  ) {
    listUserSentMessagesByToken(
      token: $token
      search: $search
      limit: $limit
      offset: $offset
    ) {
      messages {
        id
        senderId
        senderString
        recipientId
        recipientString
        subject
        body
        isRead
        isPinned
        isDeleted
        createdAt
        rawBody
        tags
      }
      pagination {
        limit
        offset
      }
      totalCount
    }
  }
`;
const MARK_READ_BY_TOKEN = gql`mutation MarkReadByToken($id: [ID!], $token: String!) {
  markUserMessageReadByToken(id: $id, token: $token)
}`;
const DELETE_BY_TOKEN = gql`mutation DeleteMessageByToken($id: ID!, $token: String!) {
  deleteUserMessageByToken(id: $id, token: $token)
}`;
const RESPOND_BY_TOKEN = gql`mutation RespondMessage($token: String!, $originalMessageId: ID!, $body: String!, $toRecipients: [String], $ccRecipients: [String]) {
  respondToAllByToken(token: $token, originalMessageId: $originalMessageId, body: $body, toRecipients: $toRecipients, ccRecipients: $ccRecipients) {
    message
  }
}`;
const SEND_MESSAGE = gql`mutation SendMessage($recipientId: ID!, $subject: String!, $body: String!) {
  createUserMessage(recipientId: $recipientId, subject: $subject, body: $body) {
    id senderId senderString recipientId subject body createdAt isRead isPinned isDeleted
  }
}`;

const UPDATE_USER = gql`mutation UpdateUser($token: String!, $updates: EmailUserDetailsInput!) {
  updateUserByToken(token: $token, updates: $updates) {
    addresses{
      name
      email
    }
    tags
    rules
  }
}`;

// Types
interface UserMessage {
  id: string;
  senderId?: number | null;
  senderString?: string | null;
  recipientId: number;
  subject: string;
  body: string;
  isRead: boolean;
  isPinned: boolean;
  isDeleted: boolean;
  createdAt: string;
}

interface ListInboxData {
  listUserInboxMessagesByToken: {
    messages: UserMessage[];
    pagination: { limit: number; offset: number };
    totalCount: number;
  };
  listUserSentMessagesByToken: {
    messages: UserMessage[];
    pagination: { limit: number; offset: number };
    totalCount: number;
  };
  searchUserInboxMessagesByToken: {
    messages: UserMessage[];
    pagination: { limit: number; offset: number };
    totalCount: number;
  };
}

interface MarkReadVars { id: string; token?: string }
interface DeleteVars { id: string; token?: string }
interface RespondVars { originalMessageId: string; body: string; token?: string, ccRecipients: any, toRecipients: any }
interface SendVars { recipientId: string; subject: string; body: string }

// Top‐level: handle 2FA and cookie
export const UserMessageByToken: React.FC = () => {
  const { token: tokenParam } = useParams<{ token: string }>();
  const cookieToken = getCookie(COOKIE_NAME);
  const initialToken = cookieToken || tokenParam || '';
  const [state, setState] = useState<{ token: string; twofactorPass: boolean }>({
    token: initialToken,
    twofactorPass: Boolean(cookieToken),
  });

  if (!state.twofactorPass) {
    return (
      <TwoFactorLogin
        token={state.token}
        setTwofactorPass={(passed, newToken) => {
          setCookie(COOKIE_NAME, newToken);
          setState({ token: newToken, twofactorPass: passed });
        }}
      />
    );
  }

  return <UserMessagesByToken token={state.token} />;
};

// Inner: fetch inbox, sent, and token data, then render UserMessages
const UserMessagesByToken: React.FC<{ token: string }> = ({ token }) => {
    const { data: tokenData, loading, error } = useQuery<{
      getUserTagsByToken: { name: string; tags: string }
    }>(GET_TOKEN_DATA, { variables: { token } });
  

  const [listInbox] = useLazyQuery<ListInboxData>(LIST_INBOX_BY_TOKEN);
  const [searchInbox] = useLazyQuery<ListInboxData>(SEARCH_USER_INBOX_MESSAGES_BY_TOKEN);
  const [listSent] = useLazyQuery<ListInboxData>(LIST_SENT_BY_TOKEN);
  const [markRead] = useMutation<void, MarkReadVars>(MARK_READ_BY_TOKEN);
  const [deleteMessage] = useMutation<boolean, DeleteVars>(DELETE_BY_TOKEN);
  const [respondMessage] = useMutation<any, RespondVars>(RESPOND_BY_TOKEN);
  const [sendMessage] = useMutation<any, SendVars>(SEND_MESSAGE);
  const [getThread] = useLazyQuery<any, any>(GET_USER_MESSAGE);
  const [updateUser] = useMutation<any, any>(UPDATE_USER);




  const hooks = {
    listInbox: async (search: string, limit: number, offset: number, filter: string) => {
      try {
        // Parse the filter to get folder and subfolder
        const [folder, subfolder] = filter.split(':');
        
        // Handle sent messages
        if (folder === 'sent') {
          console.log('Using listUserSentMessagesByToken query');
          console.log('Variables:', { search, limit, offset, token });
          const result = await listSent({ 
            variables: { search, limit, offset, token }, 
            fetchPolicy: 'network-only' 
          });
          console.log('Sent result:', result);
          console.log('Sent data:', result.data);
          console.log('Sent error:', result.error);
          return result;
        }
        
        // Handle inbox messages
        // If there's a search term, use the dedicated search function
        if (search && search.trim()) {
          console.log('Using searchUserInboxMessagesByToken query with term:', search.trim());
          const result = await searchInbox({ 
            variables: { search: search.trim(), limit, offset, filter, token }, 
            fetchPolicy: 'network-only' 
          });
          console.log('Search result:', result);
          return result;
        }
        // Otherwise use the regular list function
        console.log('Using regular listUserInboxMessagesByToken query');
        const result = await listInbox({ 
          variables: { search, limit, offset, filter, token }, 
          fetchPolicy: 'network-only' 
        });
        console.log('List result:', result);
        return result;
      } catch (error) {
        console.error('Error in listInbox hook:', error);
        throw error;
      }
    },
    markRead: async (id: string) => markRead({ variables: { id, token } }),
    deleteMessage: async (id: string) => deleteMessage({ variables: { id, token } }),
    respondMessage: async (id: string, body: string, toRecipients: string[], ccRecipients: string[]) =>  respondMessage({ variables: { originalMessageId: id, body, token, toRecipients, ccRecipients } }),
    sendMessage: async (rid: string, subject: string, body: string) => sendMessage({ variables: { recipientId: rid, subject, body } }),
    getThread: async (id: string) => getThread({ variables: { id, markRead: true, token }, fetchPolicy: 'network-only' }),
    updateUser: async (updates: any) => updateUser({ variables: { updates, token } }),
  };

  if (loading) return <div className="p-4 text-sm">Loading user…</div>;
  if (error) return <div className="p-4 text-sm text-red-600">Failed to load user data</div>;

  return (
    <UserMessages
      userData  = {tokenData?.getUserTagsByToken}
      userEmail={tokenData?.getUserTagsByToken?.name}
      userTags={tokenData?.getUserTagsByToken?.tags}
      hooks={hooks}
      logout={true}

    />
  );
};
